import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FlexxiQuote } from "@gaole/shared";
import { api, ApiError } from "../../lib/api";
import { rm } from "../../lib/format";
import { openPaymentGateway } from "../../lib/payments";
import { paymentMethodLabel, primaryPayButtonLabel, sortPaymentMethods } from "../../lib/paymentMethods";
import { useAuth } from "../../stores/auth";
import { toast } from "../../stores/toast";
import { Button } from "../ui";
import { PolicyConsentCheckbox } from "../PolicyConsentCheckbox";
import { FlexxiPlanCollapsible } from "../flexxi/FlexxiPlanCollapsible";
import { FlexxiBreakdownTable } from "../flexxi/FlexxiBreakdownTable";
import { FLEXXI_KYC_GATE_CODE } from "@gaole/shared";

interface FlexxiShopPanelProps {
  productId: string;
  quantity: number;
  /** Checkout payment picker — no collapsible; KYC gating handled by parent. */
  embedded?: boolean;
}

const fieldClass =
  "mt-1 w-full rounded-xl border border-white/10 bg-surface-2 px-3 py-2 text-sm text-white outline-none focus:border-white/20 focus:ring-0";

function PanelWrap({ embedded, hint, children }: { embedded?: boolean; hint?: string; children: ReactNode }) {
  if (embedded) return <div className="space-y-3">{children}</div>;
  return <FlexxiPlanCollapsible hint={hint}>{children}</FlexxiPlanCollapsible>;
}

export function flexxiQuoteHint(quote: FlexxiQuote | undefined): string | undefined {
  if (!quote) return undefined;
  const later = quote.installments.filter((i) => !i.dueToday).length;
  return `${rm(quote.dueToday)} today · then ${later} × ${rm(quote.monthlyAmount)}`;
}

export function FlexxiShopPanel({ productId, quantity, embedded }: FlexxiShopPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, config, moduleEnabled } = useAuth();
  const [templateId, setTemplateId] = useState("");
  const [method, setMethod] = useState("");
  const [acceptPolicies, setAcceptPolicies] = useState(false);
  const [busy, setBusy] = useState(false);

  const templatesQ = useQuery({
    queryKey: ["flexxi", "templates"],
    queryFn: () =>
      api<{
        templates: { id: string; code: string; name: string; termMonths: number }[];
        slots: { active: number; limit: number; reputationScore: number };
        kyc: { kycVerified: boolean; verifiedViaSeller?: boolean };
      }>("/flexxi/templates"),
    enabled: Boolean(user && config?.flexxiEnabled),
  });

  const quoteQ = useQuery({
    queryKey: ["flexxi", "quote", productId, quantity, templateId],
    queryFn: () =>
      api<{ quote: FlexxiQuote }>("/flexxi/quote", {
        method: "POST",
        body: { templateId, productId, channel: "SHOP", quantity },
      }),
    enabled: Boolean(user && templateId && config?.flexxiEnabled && templatesQ.data?.kyc?.kycVerified),
  });

  const walletQ = useQuery({
    queryKey: ["wallet"],
    queryFn: () => api<{ balance: number }>("/wallet"),
    enabled: Boolean(user && config?.creditsEnabled),
  });

  const methods = useMemo(() => {
    const pm = config?.paymentMethods;
    const list: string[] = [];
    if (pm?.bankIn) list.push("BANK_IN");
    if (config?.creditsEnabled) list.push("CREDITS");
    if (pm?.gkash) list.push("GKASH");
    if (pm?.stripe) list.push("STRIPE");
    return sortPaymentMethods(list);
  }, [config]);

  const quote = quoteQ.data?.quote;
  const slots = templatesQ.data?.slots;
  const templates = templatesQ.data?.templates ?? [];

  useEffect(() => {
    if (!templateId && templates[0]) setTemplateId(templates[0].id);
  }, [templateId, templates]);
  const kycVerified = templatesQ.data?.kyc?.kycVerified ?? false;
  const dueLabel = quote ? rm(quote.dueToday) : "—";

  async function startFlexxiCheckout() {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!templateId || !method) {
      toast("error", "Choose a plan and payment method");
      return;
    }
    if (!acceptPolicies) {
      toast("error", "Accept the store policies to continue");
      return;
    }
    if (!quote) {
      toast("error", "Couldn't load plan quote — try again");
      return;
    }
    setBusy(true);
    try {
      const res = await api<{
        orderId: string;
        orderNo: string;
        payment: { paid?: boolean; status?: string; redirectUrl?: string; form?: { url: string; fields: Record<string, string> } };
      }>("/flexxi/checkout/deposit", {
        method: "POST",
        body: {
          templateId,
          productId,
          channel: "SHOP",
          quantity,
          method,
          idempotencyKey: crypto.randomUUID(),
          acceptPolicies: true as const,
        },
      });

      const payment = res.payment;
      if (payment.paid) {
        toast("success", "FlexxiPlan started — card is in your vault");
        await queryClient.invalidateQueries({ queryKey: ["vault"] });
        await queryClient.invalidateQueries({ queryKey: ["flexxi"] });
        navigate("/inventory");
        return;
      }
      if (payment.status === "REDIRECT" && payment.redirectUrl) {
        openPaymentGateway({ status: "REDIRECT", redirectUrl: payment.redirectUrl });
        return;
      }
      if (payment.status === "FORM" && payment.form) {
        openPaymentGateway({ status: "GKASH_FORM", form: payment.form });
        return;
      }
      navigate(`/checkout/order/${res.orderId}`);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't start FlexxiPlan");
    } finally {
      setBusy(false);
    }
  }

  if (!moduleEnabled("instalments") || !config?.flexxiEnabled) return null;

  if (embedded) {
    if (!user || !kycVerified) return null;
    if (templatesQ.isLoading) {
      return <p className="text-[11px] text-white/45">Loading FlexxiPlan…</p>;
    }
    if (templatesQ.isError || !templates.length) return null;
  } else {
    if (!user) {
      return (
        <FlexxiPlanCollapsible>
          <p className="text-[11px] text-white/45">Sign in to pay the first instalment today and spread the rest monthly.</p>
          <Button variant="outline" className="w-full !min-h-9 text-xs" onClick={() => navigate("/login")}>
            Sign in
          </Button>
        </FlexxiPlanCollapsible>
      );
    }

    if (templatesQ.isLoading) return null;

    if (templatesQ.isError || !templates.length) return null;

    if (!kycVerified) {
      return (
        <FlexxiPlanCollapsible>
          <p className="text-[11px] leading-relaxed text-white/45">
            Verify your identity once to pay with instalments (FlexxiPlan).
          </p>
          <Button variant="outline" className="w-full !min-h-9 text-xs" onClick={() => navigate("/account/flexxi/verify")}>
            Verify for FlexxiPlan
          </Button>
        </FlexxiPlanCollapsible>
      );
    }
  }

  return (
    <PanelWrap embedded={embedded} hint={flexxiQuoteHint(quote)}>
      <p className="text-[11px] leading-relaxed text-white/45">
        {quote?.upfrontMode === "DEPOSIT" ? "Deposit + fee due today" : "First instalment due today"}; card held in
        your vault until fully paid.
        {slots ? (
          <span className="block pt-1 text-white/35">
            Active plans {slots.active}/{slots.limit}
          </span>
        ) : null}
      </p>

      <label className="block text-[10px] uppercase tracking-wide text-white/40">
        Plan
        <select className={fieldClass} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          <option value="">Select term…</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.termMonths} months)
            </option>
          ))}
        </select>
      </label>

      {quoteQ.isFetching && templateId ? <p className="text-[11px] text-white/40">Calculating…</p> : null}

      {quote && !quoteQ.isError ? (
        <FlexxiBreakdownTable quote={quote} />
      ) : quoteQ.isError ? (
        <p className="text-[11px] text-amber-400/90">
          {quoteQ.error instanceof ApiError && quoteQ.error.code === FLEXXI_KYC_GATE_CODE ? (
            <>
              {quoteQ.error.message}{" "}
              <button type="button" className="underline" onClick={() => navigate("/account/flexxi/verify")}>
                Verify now
              </button>
            </>
          ) : quoteQ.error instanceof ApiError ? (
            quoteQ.error.message
          ) : (
            "This item isn't eligible for FlexxiPlan"
          )}
        </p>
      ) : null}

      {quote && methods.length > 0 ? (
        <>
          <label className="block text-[10px] uppercase tracking-wide text-white/40">
            Pay today with
            <select value={method} onChange={(e) => setMethod(e.target.value)} className={fieldClass}>
              <option value="">Select…</option>
              {methods.map((m) => (
                <option key={m} value={m}>
                  {paymentMethodLabel(m, {
                    walletBalance: walletQ.data?.balance,
                    creditConversionRate: config?.creditConversionRate,
                  })}
                </option>
              ))}
            </select>
          </label>
          <PolicyConsentCheckbox variant="checkout" checked={acceptPolicies} onChange={setAcceptPolicies} />
          <Button
            className="w-full"
            disabled={busy || !method || quoteQ.isFetching}
            onClick={() => void startFlexxiCheckout()}
          >
            {busy ? "Starting…" : method ? primaryPayButtonLabel(method, dueLabel) : "Choose payment method"}
          </Button>
        </>
      ) : null}
    </PanelWrap>
  );
}
